/**
 * Fitness Fort – Newsletter popup.
 * Auto-opens a short delay after page load; remembers dismissal so it
 * doesn't keep interrupting the same visitor.
 */
(function () {
  const DISMISS_KEY = 'ff_newsletter_popup_dismissed';
  const DEFAULT_SUCCESS = 'Thanks for subscribing!';
  const DEFAULT_ERROR = 'Please enter a valid email address.';
  const CLOSE_AFTER_SUCCESS_MS = 1000;

  let closePopup = function () {};

  function alreadyDismissed() {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function markDismissed() {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch (error) {
      /* localStorage unavailable (private mode, etc.) — nothing to persist */
    }
  }

  function getSuccessText(form) {
    const node = form.querySelector('[data-ff-newsletter-popup-success-text]');
    return node?.textContent.trim() || DEFAULT_SUCCESS;
  }

  function showFeedback(form, type, message) {
    const feedback = form.querySelector('[data-ff-newsletter-popup-feedback]');
    if (!feedback) return;
    feedback.hidden = false;
    feedback.classList.remove('is-success', 'is-error');
    feedback.classList.add(type === 'success' ? 'is-success' : 'is-error');
    feedback.textContent = message;
    feedback.setAttribute('role', type === 'success' ? 'status' : 'alert');
  }

  function parseResponse(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const errorNode = doc.querySelector('.ff-footer__newsletter-error, #FFContactFooter-error');
    const successNode = doc.querySelector('.ff-footer__newsletter-success, #FFContactFooter-success');

    if (successNode && successNode.textContent.trim()) {
      return { type: 'success', message: successNode.textContent.trim() };
    }
    if (errorNode && errorNode.textContent.trim()) {
      return { type: 'error', message: errorNode.textContent.trim() };
    }
    if (/already been taken|already subscribed|already exists/i.test(html)) {
      return { type: 'success', message: null };
    }
    return { type: 'success', message: null };
  }

  async function submitNewsletter(form, input, submit) {
    const email = input.value.trim();
    input.value = email;

    if (!email || !input.checkValidity()) {
      input.reportValidity();
      input.focus();
      return;
    }

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');

    const body = new URLSearchParams({
      form_type: 'customer',
      utf8: '✓',
      'contact[tags]': 'newsletter',
      'contact[email]': email,
    });

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        headers: {
          Accept: 'text/html',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
      });

      const html = await response.text();
      const result = parseResponse(html);
      showFeedback(form, result.type, result.message || getSuccessText(form));

      if (result.type === 'success') {
        markDismissed();
        input.setAttribute('aria-invalid', 'false');
        window.setTimeout(closePopup, CLOSE_AFTER_SUCCESS_MS);
      } else {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
      }
    } catch (error) {
      showFeedback(form, 'error', DEFAULT_ERROR);
      input.focus();
    } finally {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  }

  function initForm() {
    const form = document.querySelector('[data-ff-newsletter-popup-form]');
    if (!form) return;

    const input = form.querySelector('[data-ff-newsletter-popup-input]');
    const submit = form.querySelector('[data-ff-newsletter-popup-submit]');
    if (!input || !submit) return;

    input.addEventListener('input', () => {
      input.setAttribute('aria-invalid', 'false');
      const feedback = form.querySelector('[data-ff-newsletter-popup-feedback]');
      if (feedback) feedback.hidden = true;
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitNewsletter(form, input, submit);
    });
  }

  function initPopup() {
    const root = document.querySelector('[data-ff-newsletter-root]');
    const scrim = document.querySelector('[data-ff-newsletter-scrim]');
    const popup = document.querySelector('[data-ff-newsletter-popup]');
    if (!root || !popup) return;

    function open() {
      if (alreadyDismissed()) return;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      scrim?.classList.add('is-open');
      scrim?.setAttribute('aria-hidden', 'false');
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      window.setTimeout(() => {
        popup.querySelector('[data-ff-newsletter-popup-input]')?.focus({ preventScroll: true });
      }, 300);
    }

    function close() {
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      scrim?.classList.remove('is-open');
      scrim?.setAttribute('aria-hidden', 'true');
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      markDismissed();
    }

    closePopup = close;

    popup.querySelectorAll('[data-ff-newsletter-close]').forEach((el) => {
      el.addEventListener('click', close);
    });
    scrim?.addEventListener('click', close);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && popup.classList.contains('is-open')) close();
    });

    if (!alreadyDismissed()) {
      const delaySeconds = parseFloat(root.dataset.delay) || 10;
      window.setTimeout(open, delaySeconds * 1000);
    }
  }

  function init() {
    initForm();
    initPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
